"use client";

import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

const focusAreas = [
  { label: "Stability", desc: "Infrastructure designed to maintain operational continuity across all deployment conditions." },
  { label: "Volume", desc: "Systems built to handle high-frequency capital deployment without structural degradation." },
  { label: "Operational Continuity", desc: "Redundancy-based architecture ensures persistent uptime and redeployment capability." },
  { label: "Performance-Aligned Economics", desc: "Fee structures tied to your capital deployment activity, not arbitrary subscription cycles." },
];

export default function AboutContent() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    ref?.current?.querySelectorAll(".reveal")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Hero */}
      <section className="relative pt-40 pb-24 bg-[#0B0F14] overflow-hidden">
        <div className="animated-grid absolute inset-0 z-0 opacity-30" />
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 40% 50% at 30% 50%, rgba(0,230,118,0.05) 0%, transparent 70%)",
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="tag-badge mb-8 reveal">About Scaled-Out</div>
              <h1 className="hero-title text-[#F0F4F8] mb-8 reveal reveal-delay-1">
                The Infrastructure{" "}
                <span className="text-[#00E676]">Behind Scale.</span>
              </h1>
              <div className="space-y-5 reveal reveal-delay-2">
                <p className="text-[#6B7A8D] text-lg leading-relaxed">
                  Scaled-Out Limited operates as an international advertising
                  infrastructure provider focused on scalable Meta account
                  systems.
                </p>
                <div className="divider-glow" />
                <div className="space-y-3">
                  {[
                    "We are not an agency.",
                    "We do not run ads.",
                    "We do not sell hype.",
                  ]?.map((line) => (
                    <div key={line} className="flex items-center gap-3">
                      <span className="text-[#00E676] font-mono text-xs">—</span>
                      <span className="text-[#F0F4F8] text-base font-medium">{line}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[#6B7A8D] text-base leading-relaxed pt-2">
                  We engineer the systems that allow advertisers to operate
                  without structural limitation.
                </p>
              </div>
            </div>

            {/* Right side — Abstract visual */}
            <div className="reveal reveal-delay-3 flex items-center justify-center">
              <div className="relative w-64 h-64">
                {/* Concentric rings */}
                {[200, 160, 120, 80, 40]?.map((size, i) => (
                  <div
                    key={size}
                    className="absolute inset-0 m-auto rounded-full border"
                    style={{
                      width: size,
                      height: size,
                      borderColor: `rgba(0,230,118,${0.04 + i * 0.04})`,
                      boxShadow:
                        i === 4 ? "0 0 20px rgba(0,230,118,0.3)" : "none",
                    }}
                  />
                ))}
                {/* Center */}
                <div
                  className="absolute inset-0 m-auto w-8 h-8 rounded-full bg-[#00E676]"
                  style={{
                    boxShadow: "0 0 30px rgba(0,230,118,0.8)",
                  }}
                />
                {/* Label */}
                <div className="absolute -bottom-10 left-0 right-0 text-center">
                  <span className="text-[#6B7A8D] font-mono text-[10px] tracking-[0.2em] uppercase">
                    Infrastructure Core
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Focus Areas */}
      <section className="py-24 bg-[#11161D]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 reveal">
            <div className="tag-badge mx-auto mb-6 inline-flex">Our Focus</div>
            <h2 className="section-title text-[#F0F4F8]">
              Built Quietly.{" "}
              <span className="text-[#00E676]">Scaled Globally.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4 reveal reveal-delay-1">
            {focusAreas?.map((area) => (
              <div key={area?.label} className="card-neon rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-4">
                  <span
                    className="w-2 h-2 rounded-full bg-[#00E676]"
                    style={{ boxShadow: "0 0 8px rgba(0,230,118,0.8)" }}
                  />
                  <h3 className="text-[#F0F4F8] font-bold text-base">{area?.label}</h3>
                </div>
                <p className="text-[#6B7A8D] text-sm leading-relaxed">{area?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Stats */}
      <section className="py-24 bg-[#0B0F14]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[rgba(0,230,118,0.06)] rounded-2xl overflow-hidden reveal">
            {[
              { val: "2022", label: "Founded" },
              { val: "15+", label: "Countries Served" },
              { val: "$25M+", label: "Monthly Volume" },
              { val: "99.2%", label: "Uptime Record" },
            ]?.map((stat) => (
              <div key={stat?.label} className="bg-[#11161D] p-8 text-center">
                <div className="text-[#00E676] font-mono font-bold text-2xl mb-2">
                  {stat?.val}
                </div>
                <div className="text-[#6B7A8D] text-[10px] uppercase tracking-[0.15em] font-bold">
                  {stat?.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </motion.div>
  );
}